import { Link } from "react-router-dom";
import { useFetch } from "@/lib/useFetch";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatCard, Money, StatusBadge, Spinner } from "@/app/ui";
import { fmtDate } from "@/lib/format";
import { BarChart, Bar, ResponsiveContainer, XAxis, Tooltip, CartesianGrid } from "recharts";
import { Wallet, TrendingDown, AlertTriangle, FileText, Receipt, Briefcase, Users, CreditCard } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const { data, loading } = useFetch("/dashboard");
  const sym = user?.business?.currency || "£";
  if (loading || !data) return <Spinner />;
  const first = user?.name?.split(" ")[0] || "there";

  return (
    <div className="animate-fade-up">
      <PageHeader title={`Welcome back, ${first}`} description="Here's how your business is doing today." />

      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        <StatCard label="Money In" value={<Money value={data.money_in} symbol={sym} />} tone="green" icon={Wallet} sub="Total received" />
        <StatCard label="Owed to You" value={<Money value={data.owed_to_you} symbol={sym} />} tone="blue" icon={Receipt} sub="Unpaid invoices" />
        <StatCard label="Overdue" value={<Money value={data.overdue} symbol={sym} />} tone="red" icon={AlertTriangle} sub="Needs chasing" />
        <StatCard label="Money Out" value={<Money value={data.money_out} symbol={sym} />} tone="amber" icon={TrendingDown} sub="Expenses" />
      </div>

      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4 mt-4">
        <StatCard label="Open Quotes" value={data.open_quotes} icon={FileText} sub={<Money value={data.open_quotes_value} symbol={sym} />} />
        <StatCard label="Live Jobs" value={data.live_jobs} icon={Briefcase} sub="In progress" />
        <StatCard label="Customers" value={data.customers} icon={Users} sub="Total" />
        <StatCard label="Profit (rough)" value={<Money value={data.money_in - data.money_out} symbol={sym} />} tone="green" sub="In minus out" />
      </div>

      <div className="grid gap-6 lg:grid-cols-3 mt-6">
        <div className="amden-card p-6 lg:col-span-2">
          <h3 className="font-semibold text-navy mb-4">Money received (last 6 months)</h3>
          {data.revenue_series?.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data.revenue_series}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
                <Tooltip formatter={(v) => `${sym}${v}`} cursor={{ fill: "#f1f5f9" }} />
                <Bar dataKey="amount" fill="#2563EB" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-slate-400 text-sm py-16 text-center">No payments recorded yet.</p>}
        </div>

        <div className="amden-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-navy">Recent invoices</h3>
            <Link to="/app/invoices" className="text-sm text-blue-accent">View all</Link>
          </div>
          <div className="space-y-3">
            {data.recent_invoices?.length ? data.recent_invoices.map((i) => (
              <Link key={i.id} to="/app/invoices" className="flex items-center justify-between py-1 group">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-navy font-mono">{i.number}</p>
                  <p className="text-xs text-slate-400 truncate">{i.customer_name}</p>
                </div>
                <div className="text-right">
                  <Money value={i.total} symbol={sym} className="text-sm text-navy" />
                  <div className="mt-0.5"><StatusBadge status={i.status} /></div>
                </div>
              </Link>
            )) : <p className="text-slate-400 text-sm">No invoices yet.</p>}
          </div>
        </div>
      </div>

      <div className="amden-card p-6 mt-6" data-testid="dashboard-online-payments">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-navy">Online payments</h3>
          <Link to="/app/invoices" className="text-sm text-blue-accent">View invoices</Link>
        </div>
        {data.recent_payments?.length ? (
          <div className="divide-y divide-slate-100">
            {data.recent_payments.map((p, i) => (
              <div key={i} data-testid={`payment-feed-row-${p.invoice_number}`} className="flex items-center justify-between py-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-navy truncate">{p.customer_name || "Customer"}</p>
                  <p className="text-xs text-slate-400 font-mono">{p.invoice_number} · {fmtDate(p.paid_at)}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span data-testid="paid-online-badge" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold">
                    <CreditCard size={12} /> Paid online
                  </span>
                  <Money value={p.amount} symbol={sym} className="text-sm font-semibold text-navy" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-slate-400 text-sm py-6 text-center">No online payments yet. Share an invoice payment link to get paid by card.</p>
        )}
      </div>
    </div>
  );
}
